import { all, call, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import ViewerSocialProfileModel from 'models/user/ViewerSocialProfile';

import { gcApi } from 'config/axios';
import DateFormatter from 'backbone/utils/date/DateFormatter';
import {
    CHALLENGE_PAGE_FETCH_AVAILABLE_BADGE_CHALLENGES,
    CHALLENGE_PAGE_FETCH_VIRTUAL_CHALLENGES,
    CHALLENGE_PAGE_FETCH_CURRENT_CHALLENGES,
    CHALLENGE_PAGE_FETCH_PAST_CHALLENGES,
    CHALLENGE_PAGE_JOIN_BADGE_CHALLENGE,
    CHALLENGE_PAGE_VIRTUAL_CHALLENGE_DETAIL_INIT,
    CHALLENGE_PAGE_FETCH_BADGE_DETAIL,
    CHALLENGE_PAGE_OPT_IN_VIRTUAL_CHALLENGE,
    CHALLENGE_PAGE_OPT_OUT_VIRTUAL_CHALLENGE,
    CHALLENGE_PAGE_OPT_OUT_AUTO_CHALLENGE,
    CHALLENGE_PAGE_UNDO_JOIN_BADGE_CHALLENGE,
    CHALLENGE_PAGE_INIT,
    RECEIVE_SYSTEM_PREFERENCE,
    CHALLENGE_PAGE_PREPARE_JOIN_CHALLENGE,
    CHALLENGE_PAGE_PRE_JOIN_CONFIRM_DIALOG_ACTION
} from 'actions/ActionTypes';
import { requestBadgeAttributes } from 'actions/badgesActions';
import {
    challengePageFetchAutoChallengeDone,
    challengePageFetchAvailableBadgeChallengesDone,
    challengePageFetchBadgeDetailDone,
    challengePageFetchVirtualChallengesDone,
    challengePageFetchCurrentChallengesDone,
    challengePageFetchPastChallengesDone,
    challengePageJoinBadgeChallengeDone,
    challengePageOptInVirtualChallengeDone,
    challengePageOptOutVirtualChallengeDone,
    challengePageSetOptOutDialog,
    challengePageUpdateAvailableBadgeChallenge,
    challengePageVirtualChallengeDetailInitDone,
    challengePageSetRoute,
    challengePageSetPreJoinConfirmDialogState,
    challengePageJoinBadgeChallenge,
    challengePageSetBadgeChallengeNotificationStatus,
    challengePagePrepareJoinChallenge
} from 'actions/challengePageActions';
import {
    selectChallengePageBadgeChallengeState,
    selectChallengePageCurrentChallengeState,
    selectChallengePagePreJoinConfirmDialogState,
    selectChallengePageVirtualChallengeDetailState
} from 'reducers/challengePage/challengePage';
// GC+

// utils
import NavigationUtil from 'utils/NavigationUtil';
import { navigateToSubscriptionPage } from 'GCP/utils/GCPNavigationUtil';

// helper
import { extractAutoChallengeResponse } from 'utils/challenge/autoChallengeHelper';

// constants (UI)
import { NOTIFICATION_STATUS, PRE_JOIN_CONFIRM_DIALOG_TYPE } from 'ChallengePage/challengePageUiConstants';

// feature flags
import { fetchSystemPreference } from 'actions/index';
import { getSystemPreferenceValue } from 'reducers/system/systemPreferences';
import {
    gcPlusBadgePointRoleKey, // Clean up ticket: CA-172570
    gcPlusExBadgesRoleKey // Clean up ticket: CA-171397
} from 'models/preferences/PreferencesMapping';

export function* initPreimumFeaturesSaga() {
    yield put(fetchSystemPreference(gcPlusBadgePointRoleKey)); // Clean up ticket: CA-172570
    yield put(fetchSystemPreference(gcPlusExBadgesRoleKey)); // Clean up ticket: CA-171397

    let gcPlusBadgePointRoleValue = null;
    let gcPlusExBadgesRoleValue = null;

    /** awaits feature flags and init feature preparation */
    while (true) {
        gcPlusBadgePointRoleValue = yield select(getSystemPreferenceValue, gcPlusBadgePointRoleKey); // Clean up ticket: CA-172570
        gcPlusExBadgesRoleValue = yield select(getSystemPreferenceValue, gcPlusExBadgesRoleKey); // Clean up ticket: CA-171397
        if (gcPlusBadgePointRoleValue !== null && gcPlusExBadgesRoleValue !== null) {
            break;
        }
        const action = yield take(RECEIVE_SYSTEM_PREFERENCE);
        if (!action) {
            continue;
        }
    }

    const gcPlusBadgePointFeatureOn = ViewerSocialProfileModel.hasRole(gcPlusBadgePointRoleValue);
    const gcPlusExBadgeFeatureOn = ViewerSocialProfileModel.hasRole(gcPlusExBadgesRoleValue);

    if (!gcPlusBadgePointFeatureOn && !gcPlusExBadgeFeatureOn) {
        return;
    }
}

export function* challengePageInitSaga() {
    /** make sure badge attributes are fetched */
    yield put(requestBadgeAttributes());
}

export function* shouldIncludeExclusive() {
    let gcPlusExBadgesRoleValue = null;
    let gcPlusExBadgeFeatureOn = null;

    gcPlusExBadgesRoleValue = yield select(getSystemPreferenceValue, gcPlusExBadgesRoleKey); // Clean up ticket: CA-171397

    while (gcPlusExBadgesRoleValue === null) {
        const action = yield take(RECEIVE_SYSTEM_PREFERENCE);
        if (!action) {
            continue;
        }
        // Clean up ticket: CA-171397
        if (action.key === gcPlusExBadgesRoleKey) {
            break;
        }
    }

    gcPlusExBadgeFeatureOn = ViewerSocialProfileModel.hasRole(gcPlusExBadgesRoleValue);
    return gcPlusExBadgeFeatureOn;
}

const typeToName = {
    1: 'RUNNING',
    2: 'CYCLING',
    3: 'SWIMMING',
    4: 'WELLNESS_STEPS_COUNT',
    5: 'WALKING',
    6: 'YOGA',
    8: 'HIKING',
    12: 'VIRTUAL_HIKE',
    13: 'VIRTUAL_CLIMB'
};

export function* challengePageFetchAvailableBadgeChallengesSaga(action) {
    let includeExclusive = action.showExclusive || undefined;
    if (includeExclusive) {
        includeExclusive = yield call(shouldIncludeExclusive);
    }

    try {
        const params = {
            start: 1,
            limit: 10000
        };
        if (includeExclusive) {
            params.includeExclusive = true;
        }
        if (action.limit > 0) {
            params.limit = action.limit + 1;
        }
        let categoryParams = '';
        if (action.categoryTypeIds.length > 0) {
            categoryParams = action.categoryTypeIds.map((item) => `challengeCategory=${typeToName[item]}`).join('&');
            categoryParams = `?${categoryParams}`;
        }
        const response = yield call(gcApi.get, `/badgechallenge-service/badgeChallenge/available${categoryParams}`, {
            params
        });
        const payload = {
            list: response.data,
            hasMore: false
        };
        if (action.limit > 0) {
            payload.hasMore = response.data.length > action.limit;
        }
        yield put(challengePageFetchAvailableBadgeChallengesDone(payload));
    } catch (error) {
        yield put(challengePageFetchAvailableBadgeChallengesDone({ error: true }));
    }
}

export function* challengePageJoinBadgeChallengeSaga(action) {
    const { challenge } = action;
    const todayDate = DateFormatter.formatISODate(new Date());
    const url = `/badgechallenge-service/badgeChallenge/${challenge.uuid}/optIn/${todayDate}`;

    try {
        yield call(gcApi.post, url, {});
        /** update list */
        yield put(
            challengePageUpdateAvailableBadgeChallenge({
                ...challenge,
                userJoined: true
            })
        );
        yield put(challengePageJoinBadgeChallengeDone({ error: false, challenge }));
    } catch (error) {
        if (error.response.status === 402) {
            // to paywall
            const featureUrl = window.location.pathname;
            navigateToSubscriptionPage(featureUrl);
        }
        /** do nothing */
        yield put(challengePageJoinBadgeChallengeDone({ error: true, challenge }));
    }
}

export function* challengePageFetchVirtualChallengesSaga(action) {
    try {
        const params = {
            start: 1,
            limit: 10000
        };
        if (action.limit > 0) {
            params.limit = action.limit + 1;
        }
        let categoryParams = '';
        if (action.categoryTypeIds.length > 0) {
            categoryParams = action.categoryTypeIds.map((item) => `challengeCategory=${typeToName[item]}`).join('&');
            categoryParams = `?${categoryParams}`;
        }
        const response = yield call(gcApi.get, `/badgechallenge-service/virtualChallenge/available${categoryParams}`, {
            params
        });
        const payload = {
            list: response.data,
            hasMore: false
        };
        if (action.limit > 0) {
            payload.hasMore = response.data.length > action.limit;
        }
        yield put(challengePageFetchVirtualChallengesDone(payload));
    } catch (error) {
        yield put(challengePageFetchVirtualChallengesDone({ error: true }));
    }
}

export function* challengePageVirtualChallengeDetailInitSaga(action) {
    yield put(requestBadgeAttributes());
    try {
        const { challengeUuid } = action;
        const response = yield call(gcApi.get, `/badgechallenge-service/badgeChallenge/${challengeUuid}`);
        yield put(challengePageVirtualChallengeDetailInitDone({ challenge: response.data }));
    } catch (error) {
        yield put(challengePageVirtualChallengeDetailInitDone({ error: true }));
    }
}

export function* challengePageFetchBadgeDetail() {
    const { challenge } = yield select(selectChallengePageVirtualChallengeDetailState);
    try {
        yield put(challengePageFetchBadgeDetailDone({ error: true, badge: null }));
        const response = yield call(gcApi.get, `/badge-service/badge/detail/v2/${challenge.badgeId}`);
        yield put(challengePageFetchBadgeDetailDone({ badge: response.data }));
    } catch (error) {
        yield put(challengePageFetchBadgeDetailDone({ error: true, badge: null }));
    }
}

export function* challengePageOptInVirtualChallengeSaga() {
    const { challenge } = yield select(selectChallengePageVirtualChallengeDetailState);
    const todayDate = DateFormatter.formatISODate(new Date());
    const url = `/badgechallenge-service/badgeChallenge/${challenge.uuid}/optIn/${todayDate}`;

    try {
        yield call(gcApi.post, url, {});
        yield put(challengePageOptInVirtualChallengeDone({}));
    } catch (error) {
        if (error.response && error.response.status === 400) {
            /** already joined other one */
            yield put(challengePageOptInVirtualChallengeDone({ error: true, tooMany: true }));
            return;
        }
        /** do nothing */
        yield put(challengePageOptInVirtualChallengeDone({ error: true }));
    }
}

export function* challengePageOptOutVirtualChallengeSaga() {
    const { challenge } = yield select(selectChallengePageVirtualChallengeDetailState);
    const url = `/badgechallenge-service/badgeChallenge/${challenge.uuid}/optOut`;

    try {
        yield call(gcApi.post, url, {});
        yield put(challengePageOptOutVirtualChallengeDone());
        yield put(challengePageSetRoute({ route: '' }));
        NavigationUtil.navigate(`/challenge`);
    } catch (error) {
        /** do nothing */
        yield put(challengePageOptOutVirtualChallengeDone(true));
    }
}

export function* challengePageFetchCurrentChallengesSaga(action) {
    let includeExclusive = action.showExclusive || undefined;
    if (includeExclusive) {
        includeExclusive = yield call(shouldIncludeExclusive);
    }
    try {
        const params = {};
        if (action.limit > 0) {
            params.start = 1;
            params.limit = action.limit + 1;
        }
        const [
            autoChallengeRes,
            groupChallengesRes,
            adHocChallengesRes,
            vivokidChallengesRes,
            teamStepsVivokidChallengesRes,
            badgeChallengesRes,
            virtualChallengesRes
        ] = yield all([
            call(gcApi.get, `/challenge-service/challenge/active`),
            call(gcApi.get, `/groupchallenge-service/groupchallenge/nonCompleted`, {
                params
            }),
            call(gcApi.get, `/adhocchallenge-service/adHocChallenge/nonCompleted`, {
                params
            }),
            call(gcApi.get, `/vivokidchallenge-service/adHocChallenge/nonCompleted`, {
                params
            }),
            call(gcApi.get, `/vivokidchallenge-service/teamChallenge/nonCompleted`, {
                params
            }),
            call(gcApi.get, `/badgechallenge-service/badgeChallenge/non-completed`, {
                params: {
                    desc: true,
                    start: 1,
                    includeExclusive: includeExclusive || undefined,
                    ...params,
                    limit: 10000 /** FIX ME LATER: should use limit provide from action but require api support */
                }
            }),
            call(gcApi.get, `/badgechallenge-service/virtualChallenge/inProgress`, {
                params: {
                    desc: true,
                    start: 1,
                    ...params,
                    limit: 10000 /** FIX ME LATER: should use limit provide from action but require api support */
                }
            })
        ]);
        yield put(
            challengePageFetchCurrentChallengesDone({
                autoChallenge: autoChallengeRes.data,
                groupChallenges: groupChallengesRes.data,
                adHocChallenges: adHocChallengesRes.data,
                vivokidChallenges: vivokidChallengesRes.data,
                teamStepsVivokidChallenges: teamStepsVivokidChallengesRes.data,
                badgeChallenges: badgeChallengesRes.data,
                virtualChallenges: virtualChallengesRes.data
            })
        );
    } catch (error) {
        yield put(challengePageFetchCurrentChallengesDone({ error: true }));
    }
}

export function* challengePageFetchAutoChallengeSaga() {
    try {
        const autoChallengeRes = yield call(gcApi.get, `/challenge-service/challenge/active`);

        yield put(
            challengePageFetchAutoChallengeDone({
                autoChallenge: autoChallengeRes.data
            })
        );
    } catch (error) {
        yield put(challengePageFetchAutoChallengeDone({ error: true }));
    }
}

export function* challengePageOptOutAutoChallengeSaga() {
    try {
        yield call(
            gcApi.post,
            '/userprofile-service/userprofile/optional-feature/wellness-autoleaderboard/opt-out',
            {}
        );
        yield put(challengePageSetOptOutDialog({ open: false, submitting: false }));
        yield fork(challengePageFetchAutoChallengeSaga);
    } catch (error) {
        yield put(challengePageSetOptOutDialog({ open: true, submitting: false }));
        yield put({
            type: 'SHOW_SNACKBAR_MESSAGE',
            show: true,
            content: { ns: 'settings', transKey: 'something_went_wrong' }
        });
    }
}

export function* challengePageFetchPastChallengesSaga(action) {
    let includeExclusive = action.showExclusive || undefined;
    if (includeExclusive) {
        includeExclusive = yield call(shouldIncludeExclusive);
    }
    try {
        const params = {};
        if (action.limit > 0) {
            params.start = 1;
            params.limit = action.limit + 1;
        }
        const [
            autoChallengeRes,
            groupChallengesRes,
            adHocChallengesRes,
            vivokidChallengesRes,
            teamStepsVivokidChallengesRes,
            badgeChallengesRes,
            virtualChallengesRes
        ] = yield all([
            call(gcApi.get, `/challenge-service/challenge/previous`),
            call(gcApi.get, `/groupchallenge-service/groupchallenge/history/userJoined`, {
                params
            }),
            call(gcApi.get, `/adhocchallenge-service/adHocChallenge/historical`, {
                params
            }),
            call(gcApi.get, `/vivokidchallenge-service/adHocChallenge/historical`, {
                params
            }),
            call(gcApi.get, `/vivokidchallenge-service/teamChallenge/historical`, {
                params
            }),
            call(gcApi.get, `/badgechallenge-service/badgeChallenge/completed`, {
                params: {
                    desc: true,
                    start: 1,
                    limit: 10000,
                    includeExclusive: includeExclusive || undefined,
                    ...params
                }
            }),
            call(gcApi.get, `/badgechallenge-service/virtualChallenge/completed`, {
                params: {
                    desc: true,
                    start: 1,
                    limit: 10000,
                    ...params
                }
            })
        ]);
        yield put(
            challengePageFetchPastChallengesDone({
                autoChallenge: autoChallengeRes.data,
                groupChallenges: groupChallengesRes.data,
                adHocChallenges: adHocChallengesRes.data,
                vivokidChallenges: vivokidChallengesRes.data,
                teamStepsVivokidChallenges: teamStepsVivokidChallengesRes.data,
                badgeChallenges: badgeChallengesRes.data,
                virtualChallenges: virtualChallengesRes.data
            })
        );
    } catch (error) {
        yield put(challengePageFetchPastChallengesDone({ error: true }));
    }
}

export function* challengePageUndoJoinBadgeChallengeSaga() {
    const badgeChallengeState = yield select(selectChallengePageBadgeChallengeState);
    const challenge = badgeChallengeState.lastJoined;
    if (!challenge) {
        return;
    }
    const url = `/badgechallenge-service/badgeChallenge/${challenge.uuid}/optOut`;

    try {
        yield call(gcApi.post, url, {});
        /** update list */
        yield put(
            challengePageUpdateAvailableBadgeChallenge({
                ...challenge,
                userJoined: false
            })
        );
    } catch (error) {
        /** do nothing */
    }
}

export function* challengePagePrepareJoinBadgeChallengeSaga(action) {
    const { challenge, hasSubscribed } = action;
    const preJoinConfirmDialogState = yield select(selectChallengePagePreJoinConfirmDialogState);

    if (challenge.premium) {
        if (!hasSubscribed) {
            // to paywall
            const featureUrl = window.location.pathname;
            navigateToSubscriptionPage(featureUrl);
            return;
        }
    }

    if (challenge.autoChallengeLink) {
        const currentChallenge = yield select(selectChallengePageCurrentChallengeState);
        const autoChallengeInfo = extractAutoChallengeResponse(currentChallenge.autoChallenge || null);

        if (
            !autoChallengeInfo.joined &&
            !preJoinConfirmDialogState.confirmed.includes(PRE_JOIN_CONFIRM_DIALOG_TYPE.AUTO_CHALLENGE)
        ) {
            /** open auto challenge confirm dialog */
            yield put(
                challengePageSetPreJoinConfirmDialogState({
                    open: true,
                    challenge,
                    dialogType: PRE_JOIN_CONFIRM_DIALOG_TYPE.AUTO_CHALLENGE,
                    confirmed: preJoinConfirmDialogState.confirmed,
                    hasSubscribed
                })
            );
            return;
        }
    }

    // no more confirmations, clean up and join challenge
    yield put(
        challengePageSetPreJoinConfirmDialogState({ open: false, challenge: null, submitting: false, confirmed: [] })
    );
    yield put(challengePageJoinBadgeChallenge(challenge));
}

export function* challengePagePreJoinConfirmDialogActionSaga(action) {
    const preJoinConfirmDialogState = yield select(selectChallengePagePreJoinConfirmDialogState);
    const { confirm } = action;

    if (preJoinConfirmDialogState.dialogType === PRE_JOIN_CONFIRM_DIALOG_TYPE.EXCLUSIVE) {
        if (confirm) {
            // clean up
            yield put(
                challengePageSetPreJoinConfirmDialogState({
                    open: false,
                    challenge: null,
                    submitting: false,
                    confirmed: []
                })
            );

            // to paywall (this dialog only show when user view plan d)
            const featureUrl = window.location.pathname;
            navigateToSubscriptionPage(featureUrl);
            return;
        }

        const newConfirmed = [...preJoinConfirmDialogState.confirmed, PRE_JOIN_CONFIRM_DIALOG_TYPE.EXCLUSIVE];

        // close dialog, update confirm, and proceed
        yield put(
            challengePageSetPreJoinConfirmDialogState({
                open: false,
                confirmed: newConfirmed,
                challenge: preJoinConfirmDialogState.challenge
            })
        );
        // go back to prepare flow
        yield fork(challengePagePrepareJoinBadgeChallengeSaga, {
            challenge: preJoinConfirmDialogState.challenge,
            hasSubscribed: preJoinConfirmDialogState.hasSubscribed
        });
        return;
    } else if (preJoinConfirmDialogState.dialogType === PRE_JOIN_CONFIRM_DIALOG_TYPE.AUTO_CHALLENGE) {
        if (!confirm) {
            // not confirm = not join challenge = close dialog directly
            // clean up
            yield put(
                challengePageSetPreJoinConfirmDialogState({
                    open: false,
                    challenge: null,
                    submitting: false,
                    confirmed: []
                })
            );
            return;
        }

        /** update state to submitting */
        yield put(challengePageSetPreJoinConfirmDialogState({ ...preJoinConfirmDialogState, submitting: true }));

        try {
            /** join auto challenge */
            yield call(
                gcApi.post,
                '/userprofile-service/userprofile/optional-feature/wellness-autoleaderboard/opt-in',
                {}
            );
            /** sync auto challenge (we don't use saga because we want to catch error */
            const autoChallengeRes = yield call(gcApi.get, `/challenge-service/challenge/active`);

            yield put(
                challengePageFetchAutoChallengeDone({
                    autoChallenge: autoChallengeRes.data
                })
            );

            const newConfirmed = [...preJoinConfirmDialogState.confirmed, PRE_JOIN_CONFIRM_DIALOG_TYPE.AUTO_CHALLENGE];
            // close dialog, update confirm, and proceed
            yield put(
                challengePageSetPreJoinConfirmDialogState({
                    open: false,
                    confirmed: newConfirmed,
                    challenge: preJoinConfirmDialogState.challenge
                })
            );
            // go back to prepare flow
            yield fork(challengePagePrepareJoinBadgeChallengeSaga, {
                challenge: preJoinConfirmDialogState.challenge,
                hasSubscribed: preJoinConfirmDialogState.hasSubscribed
            });
            return;
        } catch (error) {
            // clean up
            yield put(
                challengePageSetPreJoinConfirmDialogState({
                    open: false,
                    challenge: null,
                    submitting: false,
                    confirmed: []
                })
            );
            // show a error dialog
            yield put(challengePageSetBadgeChallengeNotificationStatus(NOTIFICATION_STATUS.ERROR));
            return;
        }
    }

    // clean up and don't do anything else
    yield put(
        challengePageSetPreJoinConfirmDialogState({ open: false, challenge: null, submitting: false, confirmed: [] })
    );
}

export const challengePageSagas = [
    initPreimumFeaturesSaga(),
    takeLatest(CHALLENGE_PAGE_INIT, challengePageInitSaga),
    takeLatest(CHALLENGE_PAGE_FETCH_AVAILABLE_BADGE_CHALLENGES, challengePageFetchAvailableBadgeChallengesSaga),
    takeLatest(CHALLENGE_PAGE_JOIN_BADGE_CHALLENGE, challengePageJoinBadgeChallengeSaga),
    takeLatest(CHALLENGE_PAGE_FETCH_VIRTUAL_CHALLENGES, challengePageFetchVirtualChallengesSaga),
    takeLatest(CHALLENGE_PAGE_VIRTUAL_CHALLENGE_DETAIL_INIT, challengePageVirtualChallengeDetailInitSaga),
    takeLatest(CHALLENGE_PAGE_FETCH_BADGE_DETAIL, challengePageFetchBadgeDetail),
    takeLatest(CHALLENGE_PAGE_OPT_IN_VIRTUAL_CHALLENGE, challengePageOptInVirtualChallengeSaga),
    takeLatest(CHALLENGE_PAGE_OPT_OUT_VIRTUAL_CHALLENGE, challengePageOptOutVirtualChallengeSaga),
    takeLatest(CHALLENGE_PAGE_FETCH_CURRENT_CHALLENGES, challengePageFetchCurrentChallengesSaga),
    takeLatest(CHALLENGE_PAGE_OPT_OUT_AUTO_CHALLENGE, challengePageOptOutAutoChallengeSaga),
    takeLatest(CHALLENGE_PAGE_FETCH_PAST_CHALLENGES, challengePageFetchPastChallengesSaga),
    takeLatest(CHALLENGE_PAGE_UNDO_JOIN_BADGE_CHALLENGE, challengePageUndoJoinBadgeChallengeSaga),
    takeLatest(CHALLENGE_PAGE_PREPARE_JOIN_CHALLENGE, challengePagePrepareJoinBadgeChallengeSaga),
    takeLatest(CHALLENGE_PAGE_PRE_JOIN_CONFIRM_DIALOG_ACTION, challengePagePreJoinConfirmDialogActionSaga)
];
